import type { TeamBudgetPaymentChoice } from '@sportninja/common/features/registration/teamBudgetPayment/types'

/**
 * Whether the payment step offers "Defer Payment". Never to a Team Budget rep
 * (SN-8441): their choice is paying now — registered once it goes through — or Not
 * now; a deferral is neither.
 */
export const isDeferredPaymentOffered = ({
  isTeamRep,
  isTeamBudgetRep,
  division,
}: {
  isTeamRep?: boolean | null
  isTeamBudgetRep?: boolean | null
  division?: {
    defer_payment?: boolean | null
    player_defer_payment?: boolean | null
  } | null
}): boolean =>
  !isTeamBudgetRep &&
  Boolean(isTeamRep ? division?.defer_payment : division?.player_defer_payment)

/**
 * What a submit on the payment step does:
 * - `team_budget_commit`   — the rep pressed Enter in their choice: record it (a typed
 *                            amount is committed). Enter never pays and never registers;
 * - `team_budget_pay`      — their priced payment: the session gate, then the provider
 *                            (the team registers once the payment goes through);
 * - `team_budget_register` — Not now, recorded: confirm the registration at $0;
 * - `zero_balance`         — nothing is owed: confirm;
 * - `pay_now`              — a card payment: the session gate, then the provider;
 * - `in_person`            — already paid in person: confirm;
 * - `blocked`              — nothing may be submitted yet (no method chosen, the choice
 *                            is not priced, a payment was already taken).
 */
export type PaymentSubmitRoute =
  | 'team_budget_commit'
  | 'team_budget_pay'
  | 'team_budget_register'
  | 'zero_balance'
  | 'pay_now'
  | 'in_person'
  | 'blocked'

export const resolvePaymentSubmitRoute = ({
  submittedBy,
  offersTeamBudgetCheckout,
  repChoice,
  isPriced,
  isLocked,
  hasNoBalanceDue,
  paymentMethod,
}: {
  /** Enter in a field (the form's own submit), or the button. */
  submittedBy: 'enter' | 'button'
  /** A Team Budget rep choosing whether to pay now, on an api2 that takes it first. */
  offersTeamBudgetCheckout: boolean
  repChoice: TeamBudgetPaymentChoice
  /** The session is priced for exactly the choice on screen. */
  isPriced: boolean
  /** A payment was already taken (or may have been) for this registration. */
  isLocked: boolean
  hasNoBalanceDue: boolean
  paymentMethod: string | null
}): PaymentSubmitRoute => {
  if (offersTeamBudgetCheckout) {
    if (isLocked) {
      return 'blocked'
    }

    if (submittedBy === 'enter') {
      return 'team_budget_commit'
    }

    if (!isPriced) {
      return 'blocked'
    }

    return repChoice === 'later' ? 'team_budget_register' : 'team_budget_pay'
  }

  if (hasNoBalanceDue) {
    return 'zero_balance'
  }

  if (paymentMethod === 'paid-in-person') {
    return 'in_person'
  }

  return paymentMethod === 'credit-card' ? 'pay_now' : 'blocked'
}
